import axios, {
  AxiosDefaults,
  AxiosHeaderValue,
  AxiosInterceptorManager,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  HeadersDefaults,
} from 'axios'
import CryptoJS from 'crypto-js'
import qs from 'query-string'

import { X_DATA_KEY } from '@/consts'
import { ignoreErrorCodes } from '@/consts/errorMsg'
import { getToken } from '@/utils/auth'
import {
  formatErrCode,
  handleRequestError,
  handleResponseError,
  reportError,
  showError,
} from '@/utils/error'

import { emitter } from './eventBus'

type Res<T> = {
  [K in keyof T]: T[K]
} & { code: number; msg: string }

export interface RequestConf<D = any> extends AxiosRequestConfig<D> {
  errMsg?: boolean | string | Record<string, string>
  noErrMsg?: boolean
  domain?: number
  headers?: Record<string, string>
}

interface AxiosInstance {
  (config: RequestConf): AxiosPromise
  (url: string, config?: RequestConf): AxiosPromise
  interceptors: {
    request: AxiosInterceptorManager<RequestConf>
    response: AxiosInterceptorManager<AxiosResponse>
  }
  getUri(config?: RequestConf): string
  request<T = any>(config: RequestConf): Promise<Res<T>>
  get<T = any>(url: string, config?: RequestConf): Promise<Res<T>>
  delete<T = any>(url: string, config?: RequestConf): Promise<Res<T>>
  head<T = any>(url: string, config?: RequestConf): Promise<Res<T>>
  options<T = any>(url: string, config?: RequestConf): Promise<Res<T>>
  post<T = any>(url: string, data?: any, config?: RequestConf): Promise<Res<T>>
  put<T = any>(url: string, data?: any, config?: RequestConf): Promise<Res<T>>
  patch<T = any>(url: string, data?: any, config?: RequestConf): Promise<Res<T>>

  defaults: Omit<AxiosDefaults, 'headers'> & {
    headers: HeadersDefaults & {
      [key: string]: AxiosHeaderValue
    }
  }
}

function decryptData(xdatastring: string) {
  try {
    const aesBlockSize = 16
    const _key = X_DATA_KEY
    const iv = CryptoJS.enc.Utf8.parse(_key.substr(0, aesBlockSize))
    const ct = CryptoJS.enc.Base64.parse(xdatastring)
    const key = CryptoJS.enc.Utf8.parse(_key)
    const decrypted = CryptoJS.AES.decrypt({ ciphertext: ct } as any, key, {
      iv: iv,
    })
    const plainJSON = decrypted.toString(CryptoJS.enc.Utf8)
    return JSON.parse(plainJSON)
  } catch {
    return {}
  }
}

export function withInterceptor(instance: AxiosInstance, source: string = 'platform') {
  instance.interceptors.request.use((config) => {
    const token = getToken()
    if (!config.headers) {
      config.headers = {}
    }

    const isAuthApi =
      (config.method === 'post' && config.url === '/login') ||
      (config.method === 'post' && config.url === '/user')

    if (token && !config.headers['Authorization'] && !isAuthApi) {
      config.headers['Authorization'] = token
    }
    return config
  }, handleRequestError)

  instance.interceptors.response.use(
    async (response) => {
      if (typeof response.data?.['x-data'] == 'string') {
        const xData = decryptData(response.data?.['x-data'])
        response.data = Object.assign(response?.data, xData)
      }
      const { code } = response.data

      if (code === 0 || code === undefined) {
        if (response.data && typeof response.data === 'object') {
          response.data.source = source
        }
        return response.data
      }

      if (code === 176) {
        emitter.emit('LOGOUT', { reason: 'API token invalid' })
        throw response.data
      }

      const { errMsg = {} } = response.config as any
      const errMap = formatErrCode(errMsg)
      let err
      if (source && errMap[source]) {
        err = errMap[source][code] || errMap[source].default || errMap[code] || errMap.default
      } else {
        err = errMap[code] || errMap.default
      }

      if (ignoreErrorCodes.includes(code)) {
        // ignore
      } else {
        if (errMsg !== false && !!err.msg) {
          showError(err.msg, code, err.type, source)
        }
        reportError(response.data, err.msg, {
          url: response.config.url,
          method: response.config.method,
          data: response.config.data,
        })
      }

      return response.data
    },
    (error) => {
      const { errMsg = {} } = error.config || {}
      const errMap = formatErrCode(errMsg)
      error._matErrMap = errMap
      return handleResponseError(error, !error?.config?.noErrMsg)
    },
  )

  return instance
}

export function createInstance(
  baseURL: string,
  source: string = 'platform',
  needIntercept = true,
): AxiosInstance {
  const service = axios.create({
    baseURL,
    timeout: 1000 * 60,
    paramsSerializer: function (params) {
      return qs.stringify(params)
    },
  })

  if (needIntercept) {
    withInterceptor(service, source)
  }

  return service
}

export default createInstance(
  import.meta.env.VITE_ENV === 'development' ? '/api' : import.meta.env.VITE_API_BASE_URL,
  'platform',
)
